import {
  Component,
  computed,
  inject,
  Inject,
  output,
  signal,
  ViewChild
} from '@angular/core';
import { AbbSidePanelComponent } from '@components/abb-side-panel/abb-side-panel.component';
import {
  SIDE_PANEL_DATA,
  SidePanelData,
} from '@components/abb-side-panel/side-panel';
import { TRANSLATION_PREFIX } from '@pipes/t.pipe';
import { AbbCommonModule } from 'app/abb-common.module';
import {
  AlarmInput,
  AlarmListQuery,
  CreateAlarmMutation,
  CreateAlarmMutationVariables,
  Status,
  UpdateAlarmMutation,
  UpdateAlarmMutationVariables,
  User,
} from 'gql/graphql';
import { AlarmFormComponent } from '../alarm-form/alarm-form.component';
import { FormGroup } from '@angular/forms';
import { Apollo } from 'apollo-angular';
import { createAlarmMutation, updateAlarmMutation } from '@graphql/alarm';
import { ToastService } from '@services/ui/toast.service';
import { TranslateService } from '@ngx-translate/core';
import { t } from '@utils/strings';

type AlarmsSidePanelData = {
  mode: 'edit' | 'new';
  defaultData?: AlarmListQuery['alarmList'][number];
};

@Component({
  selector: 'abb-alarms-side-panel',
  standalone: true,
  imports: [AbbCommonModule, AbbSidePanelComponent, AlarmFormComponent],
  template: `
    <abb-side-panel #panelRef contentWrapperClass="!px-0">
      <abb-alarm-form
        [isLoading]="saving()"
        [mode]="data.mode"
        [formData]="initValues()"
        (formSubmit)="onSubmit($event)"
        (onCancel)="onCancel()"
      ></abb-alarm-form>
    </abb-side-panel>
  `,
  styles: ``,
  providers: [
    {
      provide: TRANSLATION_PREFIX,
      useValue: 'alarms.tabs.list',
    },
  ],
})
export class AlarmsSidePanelComponent
  implements SidePanelData<AlarmsSidePanelData>
{
  onSubmitted = output<boolean>();

  saving = signal(false);
  submitBtnDisabled: boolean = true;
  @ViewChild('panelRef') panelRef: AbbSidePanelComponent | undefined;
  prefix = inject(TRANSLATION_PREFIX);
  initValues = computed(() => {
    return this.data.mode === 'edit' ? this.data.defaultData : undefined;
  });

  constructor(
    private readonly apollo: Apollo,
    @Inject(SIDE_PANEL_DATA) public data: AlarmsSidePanelData,
    private readonly toastService: ToastService,
    private readonly translate: TranslateService
  ) {}

  onCancel() {
    this.panelRef?.dispose();
  }

  onSubmit(form: FormGroup): void {
    if (form.invalid) {
      return;
    }
    this.saving.set(true);

    const alarmInput: AlarmInput = {
      propertiesCriteria: form.value.assets.propertiesCriteria,
      groupsCriteria: form.value.assets.groupsCriteria,
      name: form.value.nameDesc.name,
      description: form.value.nameDesc.description,
      status: Status.Ok,
      delay: form.value.condition.delay,
      category: form.value.condition.category,
      lastUpdate: new Date(),
      condition: form.value.condition.alarmCondition,
      enabled: false,
      assets: form.value.assets.assets,
      assetType: form.value.assets.assetType,
      type: form.value.condition.type,
      thresholdValue: form.value.condition.thresholdValue,
      thresholdType: form.value.condition.thresholdType,
      metric: form.value.condition.metric.id,
      notificationsContacts: form.value.notifications.contacts.map(
        (c: User) => c.id
      ),
    };

    if (this.data.mode === 'new') {
      this.apollo
        .mutate<CreateAlarmMutation, CreateAlarmMutationVariables>({
          mutation: createAlarmMutation,
          variables: { input: alarmInput },
        })
        .subscribe({
          next: () => {
            this.saving.set(false);
            this.toastService.notifySuccess(
              t(
                this.prefix,
                `{name} was created successfully`,
                this.translate,
                { name: form.value.nameDesc.name }
              )
            );
            this.panelRef?.dispose();
            this.onSubmitted.emit(true);
            this.apollo.client.refetchQueries({
              include: ['AlarmList'],
            });
          },
          error: () => {
            this.saving.set(false);
            this.toastService.notifyError(
              t(
                this.prefix,
                `An error occurred while creating {name}`,
                this.translate,
                { name: this.data.defaultData?.name }
              )
            );
            this.onSubmitted.emit(false);
          },
        });
    } else {
      this.apollo
        .mutate<UpdateAlarmMutation, UpdateAlarmMutationVariables>({
          mutation: updateAlarmMutation,
          variables: { id: this.data.defaultData?.id!!, input: alarmInput },
        })
        .subscribe({
          next: () => {
            this.saving.set(false);
            this.toastService.notifySuccess(
              t(
                this.prefix,
                `{name} was updated successfully`,
                this.translate,
                { name: this.data.defaultData?.name }
              )
            );
            this.panelRef?.dispose();
            this.onSubmitted.emit(true);
            this.apollo.client.refetchQueries({
              include: ['AlarmList'],
            });
          },
          error: () => {
            this.saving.set(false);
            this.toastService.notifyError(
              t(
                this.prefix,
                `An error occurred while updating {name}`,
                this.translate,
                { name: this.data.defaultData?.name }
              )
            );
            this.onSubmitted.emit(false);
          },
        });
    }
  }
}
